/* E-Mail-Archiv-Verwaltung fur Souvera-Clouds (Superadmin-only).
 *
 * Aktivierung mit Aufbewahrungsfrist + automatischer Loschung,
 * Status-Anzeige (Nachrichtenanzahl, letzter Ingest/Seal),
 * Policy-Update und Deaktivierung.
 * Backend: /api/admin/clouds/{id}/archive[/enable|/disable|/status|/policy].
 */
import React, { useState, useRef, useEffect } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import {
    Archive, Play, Square, RefreshCw, CheckCircle2,
    XCircle, Loader2, Clock, Hash, Trash2, HardDrive, AlertTriangle,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function KV({ label, mono, children, testid }) {
    return (
        <div className="flex items-start justify-between gap-3 py-1.5 border-b border-zinc-800/60 last:border-0">
            <span className="text-xs text-zinc-500 shrink-0">{label}</span>
            <span
                data-testid={testid}
                className={`text-xs text-zinc-200 text-right break-all ${mono ? "font-mono" : ""}`}
            >
                {children}
            </span>
        </div>
    );
}

function fmtAgo(iso) {
    if (!iso) return "—";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
}

export default function ArchivePanel({ cloudId }) {
    const statusKey = `/api/admin/clouds/${cloudId}/archive/status`;
    const { data: st, mutate, isLoading, error: fetchErr } = useSWR(statusKey, fetcher, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
        errorRetryCount: 0,
        errorRetryInterval: 0,
    });

    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [showEnable, setShowEnable] = useState(false);
    const [retention, setRetention] = useState(10);
    const [autoDelete, setAutoDelete] = useState(true);
    const [dismissedError, setDismissedError] = useState(false);
    const [enableJobId, setEnableJobId] = useState(null);

    const { data: enableJob } = useSWR(
        enableJobId ? `/api/admin/jobs/${enableJobId}` : null,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshInterval: (latest) => {
                if (!latest) return 3000;
                const status = latest?.job?.status;
                if (status === "running" || status === "queued") return 3000;
                return 0;
            },
        },
    );

    const lastJobIdRef = useRef(null);
    useEffect(() => {
        if (!enableJob?.job) return;
        if (lastJobIdRef.current === enableJobId) return;
        const status = enableJob.job.status;
        if (status === "succeeded") {
            lastJobIdRef.current = enableJobId;
            setEnableJobId(null);
            setShowEnable(false);
            setBusy(null);
            mutate();
        } else if (status === "failed") {
            lastJobIdRef.current = enableJobId;
            setEnableJobId(null);
            setBusy(null);
            setErr(enableJob.job.error || "Archiv-Aktivierung fehlgeschlagen");
        }
    }, [enableJob, enableJobId, mutate]);

    const run = async (kind, fn) => {
        setBusy(kind);
        setErr(null);
        try {
            await fn();
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const doEnable = async () => {
        setBusy("enable");
        setErr(null);
        try {
            const r = await api.post(`${statusKey.replace("/status", "")}/enable`, {
                retention_years: retention,
                auto_delete: autoDelete,
            });
            setEnableJobId(r.data.job_id);
        } catch (e) {
            setErr(formatApiError(e));
            setBusy(null);
        }
    };

    const doDisable = () =>
        run("disable", async () => {
            await api.post(`${statusKey.replace("/status", "")}/disable`);
        });

    const doPolicy = () =>
        run("policy", async () => {
            await api.post(`${statusKey.replace("/status", "")}/policy`, {
                retention_years: retention,
                auto_delete: autoDelete,
            });
        });

    const doReconfigure = () =>
        run("reconfigure", async () => {
            await api.post(`${statusKey.replace("/status", "")}/reconfigure`);
        });

    if (isLoading && !st) {
        return (
            <div className="cm-card border border-zinc-800" data-testid="archive-panel" style={{ minHeight: 120 }}>
                <div className="flex items-center justify-center py-6">
                    <Loader2 size={14} className="animate-spin text-zinc-500" />
                </div>
            </div>
        );
    }

    const enabled = st?.enabled;
    const showFetchErr = fetchErr && !dismissedError;
    const statusBadge = enabled ? (
        <span className="px-1.5 py-0.5 rounded-sm text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
            <CheckCircle2 size={10} /> Aktiv
        </span>
    ) : (
        <span className="px-1.5 py-0.5 rounded-sm text-[11px] font-mono bg-zinc-700/30 text-zinc-500 border border-zinc-700/50 flex items-center gap-1">
            <XCircle size={10} /> Inaktiv
        </span>
    );

    return (
        <div className="cm-card border border-zinc-800" data-testid="archive-panel" style={{ minHeight: 120 }}>
            {/* Header */}
            <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Archive size={14} className={enabled ? "text-emerald-400" : "text-zinc-600"} />
                    <span className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                        E-Mail-Archiv
                    </span>
                    {statusBadge}
                </div>
                <div className="flex items-center gap-2">
                    {!enabled && !showEnable && (
                        <button
                            onClick={() => setShowEnable(true)}
                            data-testid="archive-enable-btn"
                            disabled={busy}
                            className="px-2.5 py-1 text-[11px] font-mono uppercase border border-emerald-700/50 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1"
                        >
                            <Play size={10} /> Enable
                        </button>
                    )}
                    {enabled && (
                        <>
                            <button
                                onClick={() => setShowEnable((v) => !v)}
                                data-testid="archive-policy-btn"
                                disabled={busy}
                                className="px-2.5 py-1 text-[11px] font-mono uppercase border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                            >
                                <RefreshCw size={10} /> Policy
                            </button>
                            <button
                                onClick={doReconfigure}
                                data-testid="archive-reconfigure-btn"
                                disabled={busy === "reconfigure"}
                                className="px-2.5 py-1 text-[11px] font-mono uppercase border border-amber-700/50 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1"
                            >
                                {busy === "reconfigure" ? (
                                    <Loader2 size={10} className="animate-spin" />
                                ) : (
                                    <RefreshCw size={10} />
                                )}
                                Reconfigure
                            </button>
                            <button
                                onClick={doDisable}
                                data-testid="archive-disable-btn"
                                disabled={busy === "disable"}
                                className="px-2.5 py-1 text-[11px] font-mono uppercase border border-red-700/50 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
                            >
                                {busy === "disable" ? (
                                    <Loader2 size={10} className="animate-spin" />
                                ) : (
                                    <Square size={10} />
                                )}
                                Disable
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Enable/Policy form */}
            {showEnable && (
                <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-900/50 space-y-3">
                    <div className="flex items-center gap-4">
                        <label className="text-[11px] text-zinc-500 font-mono uppercase">
                            Aufbewahrung
                            <input
                                type="number"
                                min={1}
                                max={30}
                                value={retention}
                                onChange={(e) => setRetention(Number(e.target.value))}
                                className="ml-2 w-16 px-1.5 py-0.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-200 font-mono rounded-sm text-right"
                                data-testid="archive-retention-input"
                            />
                            <span className="ml-1 text-zinc-600">Jahre</span>
                        </label>
                        <label className="text-[11px] text-zinc-500 font-mono uppercase flex items-center gap-1.5">
                            <input
                                type="checkbox"
                                checked={autoDelete}
                                onChange={(e) => setAutoDelete(e.target.checked)}
                                className="rounded-sm"
                                data-testid="archive-autodelete-check"
                            />
                            Auto-Loschen
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        {enableJobId && enableJob?.job && (enableJob.job.status === "queued" || enableJob.job.status === "running") ? (
                            <>
                                <div className="flex-1 flex items-center gap-2">
                                    <Loader2 size={12} className="animate-spin text-emerald-400" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] text-emerald-400 font-mono truncate">
                                            {enableJob.job.current_step || "..."}
                                        </div>
                                        <div className="w-full bg-zinc-800 h-1 mt-0.5 rounded-sm overflow-hidden">
                                            <div
                                                className="h-full bg-emerald-500 transition-all duration-500"
                                                style={{ width: `${enableJob.job.progress || 0}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-zinc-500 font-mono shrink-0">
                                        {enableJob.job.progress || 0}%
                                    </span>
                                </div>
                                <button
                                    onClick={() => setShowEnable(false)}
                                    className="px-3 py-1 text-[11px] font-mono text-zinc-500 hover:text-zinc-300 shrink-0"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                {!enabled && (
                                    <button
                                        onClick={doEnable}
                                        disabled={busy === "enable"}
                                        data-testid="archive-confirm-enable"
                                        className="px-3 py-1 text-[11px] font-mono uppercase border border-emerald-600 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1"
                                    >
                                        {busy === "enable" ? (
                                            <Loader2 size={10} className="animate-spin" />
                                        ) : (
                                            <Play size={10} />
                                        )}
                                        Archiv aktivieren
                                    </button>
                                )}
                                {enabled && (
                                    <button
                                        onClick={doPolicy}
                                        disabled={busy === "policy"}
                                        data-testid="archive-confirm-policy"
                                        className="px-3 py-1 text-[11px] font-mono uppercase border border-zinc-600 bg-zinc-700/20 text-zinc-300 hover:bg-zinc-700/40 flex items-center gap-1"
                                    >
                                        {busy === "policy" ? (
                                            <Loader2 size={10} className="animate-spin" />
                                        ) : (
                                            <RefreshCw size={10} />
                                        )}
                                        Policy speichern
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowEnable(false)}
                                    className="px-3 py-1 text-[11px] font-mono text-zinc-500 hover:text-zinc-300"
                                >
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Error */}
            {err && (
                <div className="px-3 py-2 border-b border-red-900/30 bg-red-500/5 text-[11px] text-red-400 font-mono flex items-start justify-between gap-2">
                    <span className="flex-1">{err}</span>
                    <button onClick={() => setErr(null)} className="text-zinc-500 hover:text-zinc-300 shrink-0"><XCircle size={12} /></button>
                </div>
            )}
            {showFetchErr && (
                <div className="px-3 py-2 border-b border-amber-900/30 bg-amber-500/5 text-[11px] text-amber-400 font-mono flex items-start justify-between gap-2">
                    <span className="flex-1">
                        <AlertTriangle size={10} className="inline mr-1 mb-px" />
                        Fehler beim Laden
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => { setDismissedError(false); mutate(); }}
                            className="text-amber-400 hover:text-amber-300 flex items-center gap-1">
                            <RefreshCw size={10} /> Reload
                        </button>
                        <button onClick={() => setDismissedError(true)}
                            className="text-zinc-500 hover:text-zinc-300">
                            <XCircle size={12} />
                        </button>
                    </div>
                </div>
            )}

            {/* Status details */}
            {enabled && (
                <div className="px-3 py-2">
                    <KV label="Nachrichten" mono testid="archive-total">
                        {st?.total_messages != null ? st.total_messages.toLocaleString() : "—"}
                    </KV>
                    <KV label="Aufbewahrung" testid="archive-retention">
                        {st?.retention_years != null ? `${st.retention_years} Jahre` : "—"}
                    </KV>
                    <KV label="Auto-Loschen" testid="archive-autodelete">
                        {st?.auto_delete ? (
                            <span className="text-emerald-400 flex items-center gap-1 justify-end">
                                <Trash2 size={10} /> An
                            </span>
                        ) : (
                            <span className="text-zinc-500">Aus</span>
                        )}
                    </KV>
                    <KV label="Letzter Ingest" mono testid="archive-last-ingest">
                        {fmtAgo(st?.last_ingest_at)}
                    </KV>
                    <KV label="Letztes Seal" mono testid="archive-last-sealed">
                        {fmtAgo(st?.last_sealed_at)}
                    </KV>
                    {st?.s3_bucket && (
                        <KV label="S3-Bucket" mono testid="archive-s3">
                            {st.s3_bucket}
                        </KV>
                    )}
                </div>
            )}

            {!enabled && !showEnable && (
                <div className="px-3 py-6 text-center text-[11px] text-zinc-600 font-mono">
                    E-Mail-Archiv ist deaktiviert.
                </div>
            )}
        </div>
    );
}
